import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckIcon, ArrowRightIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';

import Header from '../components/layout/Header';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { useAuth } from '../contexts/AuthContext';
import {
  billingApi,
  formatPrice,
  UNLIMITED,
  type Entitlements,
  type Plan,
} from '../services/api/billing';

const limitLabel = (value: number, noun: string) =>
  value === UNLIMITED ? `Unlimited ${noun}` : `${value} ${noun}`;

const planFeatures = (plan: Plan): string[] => [
  'Full relevance-ranked job feed',
  limitLabel(plan.max_tracked_applications, 'tracked applications'),
  plan.max_cv_analyses === UNLIMITED
    ? 'Unlimited CV analyses'
    : `${plan.max_cv_analyses} CV ${plan.max_cv_analyses === 1 ? 'analysis' : 'analyses'}${plan.days ? ' per pass' : ''}`,
  ...(plan.interview_roadmap ? ['Interview roadmap and notes'] : []),
  ...(plan.job_alerts ? ['Alerts for new matching roles'] : []),
];

const PricingPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [plans, setPlans] = useState<Plan[]>([]);
  const [entitlements, setEntitlements] = useState<Entitlements | null>(null);
  const [paymentsEnabled, setPaymentsEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        const data = await billingApi.getPlans();
        setPlans(data.plans);
        setPaymentsEnabled(data.payments_enabled);
        if (user) setEntitlements(await billingApi.getEntitlements());
      } catch {
        toast.error('Could not load pricing. Please refresh.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [user]);

  const choose = async (plan: Plan) => {
    if (!user) {
      navigate('/register', { state: { from: { pathname: '/pricing' } } });
      return;
    }
    if (plan.amount === 0) {
      navigate('/jobs');
      return;
    }
    setBusy(plan.code);
    try {
      const { authorization_url } = await billingApi.startCheckout(plan.code);
      // Hand off to Paystack's hosted page - card details never touch JOE
      window.location.href = authorization_url;
    } catch (error: any) {
      toast.error(error?.response?.data?.detail ?? 'Could not start checkout.');
      setBusy(null);
    }
  };

  if (loading) return <LoadingSpinner />;

  const currentCode = entitlements?.plan.code;

  return (
    <>
      <Header />
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white pt-28 pb-24">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
              Pay for the search, not a subscription
            </h1>
            <p className="text-lg text-gray-600">
              Passes expire on their own. Nothing renews automatically, so you are
              never billed for a tool you stopped needing.
            </p>
            {entitlements?.is_paid && entitlements.expires_at && (
              <p className="mt-4 inline-block text-sm font-medium text-blue-700 bg-blue-50 px-4 py-2 rounded-full">
                {entitlements.plan.name} active until{' '}
                {new Date(entitlements.expires_at).toLocaleDateString()}
              </p>
            )}
          </div>

          {!paymentsEnabled && (
            <div className="max-w-3xl mx-auto mb-10 px-4 py-3 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-sm text-center">
              Checkout is not configured yet, so paid plans cannot be purchased.
            </div>
          )}

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto items-start">
            {plans.map((plan, index) => {
              const isCurrent = plan.code === currentCode;
              return (
                <motion.div
                  key={plan.code}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.08 }}
                  className={`relative bg-white rounded-3xl p-8 border transition-all ${
                    plan.highlight
                      ? 'border-blue-600 shadow-xl md:scale-105'
                      : 'border-gray-200 shadow-sm hover:shadow-lg'
                  }`}
                >
                  {plan.highlight && (
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-blue-600 to-purple-600 text-white text-xs font-bold px-4 py-1.5 rounded-full">
                      MOST POPULAR
                    </span>
                  )}

                  <h2 className="text-xl font-bold text-gray-900">{plan.name}</h2>
                  <p className="text-sm text-gray-500 mt-1 mb-6 min-h-[40px]">{plan.tagline}</p>

                  <div className="mb-6">
                    <span className="text-4xl font-extrabold text-gray-900">
                      {plan.amount === 0 ? 'Free' : formatPrice(plan)}
                    </span>
                    {plan.days > 0 && (
                      <span className="text-gray-500 text-sm ml-2">
                        for {plan.days} days
                      </span>
                    )}
                  </div>

                  <ul className="space-y-3 mb-8">
                    {planFeatures(plan).map((feature) => (
                      <li key={feature} className="flex items-start gap-3">
                        <CheckIcon className="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
                        <span className="text-sm text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <button
                    onClick={() => choose(plan)}
                    disabled={isCurrent || busy !== null || (plan.amount > 0 && !paymentsEnabled)}
                    className={`w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                      plan.highlight
                        ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:shadow-lg'
                        : 'bg-gray-900 text-white hover:bg-gray-800'
                    }`}
                  >
                    {isCurrent
                      ? 'Your current plan'
                      : busy === plan.code
                      ? 'Opening checkout...'
                      : plan.amount === 0
                      ? 'Start free'
                      : `Get ${plan.name}`}
                    {!isCurrent && busy !== plan.code && <ArrowRightIcon className="w-4 h-4" />}
                  </button>
                </motion.div>
              );
            })}
          </div>

          <p className="text-center text-sm text-gray-500 mt-12">
            Payments are processed by Paystack. Card details never reach JOE.
          </p>
        </div>
      </div>
    </>
  );
};

export default PricingPage;
